// The sidebar's items, in live Created.ai's order and groups, and the section each team and staff
// page belongs to. Live shows every item; ours link where a screen exists and say "Coming soon"
// elsewhere (design-system spec 2.5), so making an item live is one `target` here.
import type { RegisteredRouter, RouteIds } from "@tanstack/react-router";
import {
  Archive,
  ChartLine,
  Image,
  LayoutGrid,
  LayoutTemplate,
  LibraryBig,
  Lightbulb,
  ListVideo,
  type LucideIcon,
  Megaphone,
  Music,
  Palette,
  Settings,
  Tag,
  Trophy,
  User,
  UserPen,
  UserRound,
  Users,
  Video,
  Waypoints,
} from "lucide-react";

export type NavTarget =
  | {
      scope: "team";
      to:
        | "/teams/$teamId"
        | "/teams/$teamId/brands"
        | "/teams/$teamId/members"
        | "/teams/$teamId/account"
        | "/teams/$teamId/mcp-server";
    }
  | {
      scope: "brand";
      to:
        | "/teams/$teamId/brands/$brandId/editor"
        | "/teams/$teamId/brands/$brandId/generate"
        | "/teams/$teamId/brands/$brandId/products"
        | "/teams/$teamId/brands/$brandId/library"
        | "/teams/$teamId/brands/$brandId/sounds"
        | "/teams/$teamId/brands/$brandId/actors"
        | "/teams/$teamId/brands/$brandId/templates";
      medium?: "video";
    }
  /** Staff only: the API checks again on every staff call. */
  | { scope: "staff"; to: "/staff" }
  | { scope: "soon" };

export interface NavItem {
  label: string;
  /** The header crumb's word where live's differs from the label (the studio pages' plural). */
  crumb?: string;
  icon: LucideIcon;
  target: NavTarget;
}

const SOON = { scope: "soon" } as const;

export const NAV_GROUPS = [
  {
    title: "Dashboard",
    items: [
      { label: "Home", icon: LayoutGrid, target: { scope: "team", to: "/teams/$teamId" } },
      {
        label: "MCP Server",
        icon: Waypoints,
        target: { scope: "team", to: "/teams/$teamId/mcp-server" },
      },
    ],
  },
  {
    title: "Studio",
    items: [
      {
        label: "Video Editor",
        icon: ListVideo,
        target: { scope: "brand", to: "/teams/$teamId/brands/$brandId/editor" },
      },
      {
        label: "Video",
        crumb: "Videos",
        icon: Video,
        target: { scope: "brand", to: "/teams/$teamId/brands/$brandId/generate", medium: "video" },
      },
      {
        label: "Image",
        crumb: "Images",
        icon: Image,
        target: { scope: "brand", to: "/teams/$teamId/brands/$brandId/generate" },
      },
      {
        label: "AI Actors",
        // Live's crumb is the URL's segment, `startCase("actors-ai")`.
        crumb: "Actors Ai",
        icon: User,
        target: { scope: "brand", to: "/teams/$teamId/brands/$brandId/actors" },
      },
      {
        label: "Sound",
        crumb: "Sounds",
        icon: Music,
        target: { scope: "brand", to: "/teams/$teamId/brands/$brandId/sounds" },
      },
      {
        label: "Templates",
        icon: LayoutTemplate,
        target: { scope: "brand", to: "/teams/$teamId/brands/$brandId/templates" },
      },
    ],
  },
  {
    title: "Assets",
    items: [
      {
        label: "Products",
        icon: Archive,
        target: { scope: "brand", to: "/teams/$teamId/brands/$brandId/products" },
      },
      {
        label: "Library",
        icon: LibraryBig,
        target: { scope: "brand", to: "/teams/$teamId/brands/$brandId/library" },
      },
      { label: "Ad Inspiration", icon: Lightbulb, target: SOON },
    ],
  },
  {
    title: "Performance",
    items: [
      { label: "Analytics", icon: ChartLine, target: SOON },
      { label: "Ads Publisher", icon: Megaphone, target: SOON },
    ],
  },
] as const satisfies readonly { title: string; items: readonly NavItem[] }[];

export const NAV_BOTTOM = [
  { label: "Brands", icon: Tag, target: { scope: "team", to: "/teams/$teamId/brands" } },
  // Every role sees Settings: its two tabs (members, access tokens) work for every role.
  { label: "Settings", icon: Settings, target: { scope: "team", to: "/teams/$teamId/members" } },
  { label: "Admin Panel", icon: Users, target: { scope: "staff", to: "/staff" } },
] as const satisfies readonly NavItem[];

/**
 * Pages opened from the header's menus rather than the sidebar. Each is a section for the crumbs
 * and the brand switcher, and the sidebar draws no item for it, as live's has none (live's crumb is
 * the path's word). Spec web/account-settings, decision 4.
 */
export const NAV_MENU = [
  { label: "Account", icon: UserPen, target: { scope: "team", to: "/teams/$teamId/account" } },
] as const satisfies readonly NavItem[];

/** A tab of the Account page: `AccountFrame` links it, and tsc fails a path with no route. */
export interface AccountTab {
  label: string;
  icon: LucideIcon;
  to: "/teams/$teamId/account" | `/teams/$teamId/account/${string}`;
}

/**
 * The Account page's tabs, in order. Each is a route of its own under the team, at `account` or
 * `account/<tab>`, so a link can open it (#1200's trophy and unlock card). A feature adds its tab
 * with one line here and its page's line in `PAGES`. Spec web/account-tabs.
 */
export const ACCOUNT_TABS = [
  { label: "Profile", icon: UserRound, to: "/teams/$teamId/account" },
  { label: "Appearance", icon: Palette, to: "/teams/$teamId/account/appearance" },
  { label: "Achievements", icon: Trophy, to: "/teams/$teamId/account/achievements" },
] as const satisfies readonly AccountTab[];

type LiveItem =
  | (typeof NAV_GROUPS)[number]["items"][number]
  | (typeof NAV_BOTTOM)[number]
  | (typeof NAV_MENU)[number];
export type Section = Exclude<LiveItem, { target: typeof SOON }>["label"];

const ITEMS: readonly NavItem[] = [
  ...NAV_GROUPS.flatMap((group): readonly NavItem[] => group.items),
  ...NAV_BOTTOM,
  ...NAV_MENU,
];

/** The live items by label: the sections a page can belong to. */
export const SECTIONS: ReadonlyMap<string, NavItem> = new Map(
  ITEMS.filter((item) => item.target.scope !== "soon").map((item) => [item.label, item]),
);

// The Pro Studio redesign's pages (`home2`) draw their own frame and name their own nav items
// (features/studio2/nav.ts), so they are none of this shell's.
type TeamPageId = Exclude<
  Extract<RouteIds<RegisteredRouter["routeTree"]>, `/signed-in/teams/$teamId/${string}`>,
  | "/signed-in/teams/$teamId/brands/$brandId"
  | `/signed-in/teams/$teamId/home2${string}`
  | `/signed-in/teams/$teamId/brands/$brandId/home2${string}`
>;
type StaffPageId = Extract<RouteIds<RegisteredRouter["routeTree"]>, `/signed-in/staff/${string}`>;

const T = "/signed-in/teams/$teamId";
const B = "/signed-in/teams/$teamId/brands/$brandId";
const S = "/signed-in/staff";

/**
 * Every team and staff page and its section. A new team or staff route fails tsc until it has an
 * entry here.
 */
const PAGES: Record<TeamPageId | StaffPageId, Section> = {
  [`${T}/`]: "Home",
  [`${T}/brands`]: "Brands",
  [`${T}/brands/new`]: "Brands",
  [`${T}/members`]: "Settings",
  [`${T}/tokens`]: "Settings",
  [`${T}/mcp-server`]: "MCP Server",
  [`${T}/account`]: "Account",
  [`${T}/account/appearance`]: "Account",
  [`${T}/account/achievements`]: "Account",
  [`${T}/jobs`]: "Image",
  [`${T}/jobs/$jobId`]: "Image",
  [`${B}/`]: "Brands",
  [`${B}/edit`]: "Brands",
  [`${B}/editor`]: "Video Editor",
  [`${B}/editor/$projectId`]: "Video Editor",
  [`${B}/editor/asset/$assetId`]: "Video Editor",
  [`${B}/products`]: "Products",
  [`${B}/products/new`]: "Products",
  [`${B}/products/$productId`]: "Products",
  [`${B}/generate`]: "Image",
  [`${B}/library`]: "Library",
  [`${B}/sounds`]: "Sound",
  [`${B}/actors`]: "AI Actors",
  [`${B}/templates`]: "Templates",
  // Live's Admin Panel is one page, its tabs in `?tab=`: its crumb and item stay on every tab.
  [`${S}/`]: "Admin Panel",
  [`${S}/teams/$teamId`]: "Admin Panel",
  [`${S}/teams/$teamId/jobs/$jobId`]: "Admin Panel",
  [`${S}/usage`]: "Admin Panel",
  [`${S}/usage/generations`]: "Admin Panel",
  [`${S}/ai-tasks`]: "Admin Panel",
  [`${S}/audit`]: "Admin Panel",
};

/** The section of the deepest matched route, or `undefined` outside the team and staff pages. */
export function sectionOf(routeId: string, search: object): Section | undefined {
  const section = (PAGES as Record<string, Section | undefined>)[routeId];
  const video = "medium" in search && search.medium === "video";
  return section === "Image" && routeId.endsWith("/generate") && video ? "Video" : section;
}
